/**
 * « My technical card » — self-service TECHNICAL data (2026-10-08).
 *
 * User decision: « chacun modifie son instrument/backline/IEM depuis My account,
 * même sans capacité ». The band-scoped rider route writes the WHOLE per-band array,
 * so it cannot be opened to a member; this component talks to the dedicated
 * `/api/me/rider-card/:bandId` route (self-service, mounted before the global
 * read-only guard), which edits ONLY the caller's own entry and ONLY its technical
 * fields.
 *
 * WHAT IS ABSENT HERE, BY DESIGN (stated on screen, not silently):
 *   • name / role / tags        → the band's classification (an editor own it);
 *   • diet / allergies          → health data, whose source is the TEAM card
 *                                 (Account → My Team card);
 *   • rack / channel routing    → the band's patch (two people must not collide).
 * The server refuses those fields anyway; showing the list avoids the "why did my
 * change not save" loop.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiGetMyRiderCard,
  apiSaveMyRiderCard,
  type MyRiderCardDTO,
} from '../api';
import { reportSaveError, reportSaveOk } from '../lib/saveStatus';
import InstrumentEditor from './InstrumentEditor';
import BacklineEditor from './BacklineEditor';
import type { BacklineItem, Instrument } from '../types';

interface Props {
  /** The band whose card we edit (the ACTIVE profile). */
  bandId: string | null;
}

type Draft = Record<string, unknown>;

export default function MyTechnicalCard({ bandId }: Props) {
  const { t } = useTranslation();
  const [card, setCard] = useState<MyRiderCardDTO | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);

  const load = useCallback(async (id: string) => {
    try {
      const c = await apiGetMyRiderCard(id);
      setCard(c);
      setDraft(c ? { ...c.fields } : {});
      setLoadError(false);
    } catch (e) {
      // ANTI-WIPE (family rule): never clear what we have on a load error — a
      // transient 401/500 must not present empty fields as the truth.
      setLoadError(true);
      reportSaveError('rider', e);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!bandId) { setCard(null); setDraft({}); setLoaded(true); return; }
    setLoaded(false);
    void load(bandId);
  }, [bandId, load]);

  const set = (key: string, value: unknown) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    if (!bandId) return;
    setSaving(true);
    try {
      const next = await apiSaveMyRiderCard(bandId, draft);
      setCard(next);
      setDraft({ ...next.fields });
      setSavedAt(Date.now());
      reportSaveOk('rider');
    } catch (e) {
      reportSaveError('rider', e);
    } finally {
      setSaving(false);
    }
  };

  if (!bandId) return null;
  if (!loaded) return <p className="text-dim">{t('common.loading')}</p>;
  if (loadError) return <p className="text-dim">{t('myTech.loadError')}</p>;
  if (!card) {
    return (
      <p className="text-dim" style={{ margin: 0 }}>
        {t('myTech.noCard')}
      </p>
    );
  }

  const isMusician = card.kind === 'musicians';
  const instruments = (draft.instruments as Instrument[] | undefined) ?? [];
  const backline = (draft.backline as BacklineItem[] | undefined) ?? [];
  const setInstruments = (list: Instrument[]) => set('instruments', list);
  const moveInstrument = (from: number, to: number) => {
    if (to < 0 || to >= instruments.length) return;
    const next = [...instruments];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setInstruments(next);
  };

  return (
    <div className="my-tech-card">
      <p className="text-dim" style={{ fontSize: 'var(--font-sm)', marginTop: 0 }}>
        {t('myTech.intro', { name: card.name })}
      </p>

      <div className="form-row">
        <div >
          <label htmlFor="mytech-iem">{t('myTech.iemType')}</label>
          <select
            id="mytech-iem"
            value={String(draft.iemType ?? 'none')}
            disabled={saving}
            onChange={(e) => set('iemType', e.target.value)}
          >
            <option value="stereo">{t('myTech.iemStereo')}</option>
            <option value="mono">{t('myTech.iemMono')}</option>
            <option value="none">{t('myTech.iemNone')}</option>
          </select>
        </div>
        <div>
          <label htmlFor="mytech-wedges">{t('myTech.wedges')}</label>
          <input
            id="mytech-wedges"
            type="number"
            min={0}
            value={Number(draft.wedges ?? 0)}
            disabled={saving}
            onChange={(e) => set('wedges', Number(e.target.value))}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', margin: '8px 0' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={!!draft.iemWired}
            disabled={saving}
            onChange={(e) => set('iemWired', e.target.checked)}
          />
          {t('myTech.iemWired')}
        </label>
        {draft.iemWired === true && (
          <>
            {(['band', 'venue'] as const).map((pv) => (
              <label key={pv} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="radio"
                  name="mytech-iem-wired-prov"
                  disabled={saving}
                  checked={((draft.iemWiredProvidedBy as 'band' | 'venue' | undefined) || 'band') === pv}
                  onChange={() => set('iemWiredProvidedBy', pv)}
                />
                {pv === 'band' ? t('pdf.misc.band') : t('pdf.misc.venue')}
              </label>
            ))}
          </>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={!!draft.wedgesStereo}
            disabled={saving}
            onChange={(e) => set('wedgesStereo', e.target.checked)}
          />
          {t('myTech.wedgesStereo')}
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={!!draft.subwoofer}
            disabled={saving}
            onChange={(e) => set('subwoofer', e.target.checked)}
          />
          {t('myTech.subwoofer')}
        </label>
      </div>

      {isMusician && (
        <>
          <h4 style={{ margin: '12px 0 4px' }}>{t('myTech.instruments')}</h4>
          {instruments.map((inst, i) => (
            <InstrumentEditor
              key={inst.id}
              instrument={inst}
              index={i}
              total={instruments.length}
              onChange={(next) => setInstruments(instruments.map((x, k) => (k === i ? next : x)))}
              onMoveUp={() => moveInstrument(i, i - 1)}
              onMoveDown={() => moveInstrument(i, i + 1)}
              onRemove={() => setInstruments(instruments.filter((_, k) => k !== i))}
            />
          ))}
          <button
            type="button"
            className="btn btn-secondary btn-small"
            disabled={saving}
            onClick={() => setInstruments([
              ...instruments,
              { id: crypto.randomUUID(), name: '', tracks: [] } as Instrument,
            ])}
          >
            + {t('myTech.addInstrument')}
          </button>

          <h4 style={{ margin: '12px 0 4px' }}>{t('myTech.backline')}</h4>
          <BacklineEditor items={backline} onChange={(items) => set('backline', items)} />
        </>
      )}

      <div className="form-actions" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save()}>
          {saving ? t('myTech.saving') : t('common.save')}
        </button>
        {savedAt > 0 && (
          <span className="text-dim" style={{ marginLeft: 8, fontSize: 'var(--font-sm)' }}>
            {t('myTech.saved')}
          </span>
        )}
      </div>

      <p className="text-dim" style={{ fontSize: 'var(--font-xs)', marginBottom: 0 }}>
        {t('myTech.notHere')}
      </p>
    </div>
  );
}
